import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";

import { foreignKeys, supplierDrawerElements, supplierIdParamName } from "data/supplierPageData";
import CompanyDetails from "components/SupplierPage/SupplierUser/CompanyDetails/CompanyDetails";
import AllFactories from "components/SupplierPage/SupplierUser/AllFactories/AllFactories";
import axios from "axios";
import { GlobalState } from "context/GlobalContext";
import FactoryDetails from "components/SupplierPage/SupplierUser/FactoryDetails/FactoryDetails";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { Button, TextField, Typography } from "@mui/material";
import KeyboardBackspaceRoundedIcon from "@mui/icons-material/KeyboardBackspaceRounded";
import Agreements from "components/SupplierPage/SupplierUser/Agreemens/Agreements";
import SupplierStatus, { suppliers_atrr } from "components/SupplierPage/SupplierUser/SupplierStatus/supplierStatus";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import {
	APPROVAL_ACTIONS,
	areAllLatestSectionsApproved,
	mergeApprovalRow,
} from "utils/supplierApprovalHelpers";
import parseErrMsgFromError from "utils/errorFunctions/parseErrorMessageFromError";
import {
	SupplierModalHeader,
	SupplierPageHeader,
	SupplierStatusChip,
	modalCancelBtnSx,
	modalSaveBtnSx,
	supplierModalPaperProps,
	supplierUi,
} from "components/SupplierPage/shared/supplierUi";

function SupplierUser() {
	const [pathName] = useOutletContext();
	const [companyData, setCompanyData] = useState();
	const [factories, setFactories] = useState();
	const [selectedFactory, setSelectedFactory] = useState();
	const [isOnboarded, setIsOnboarded] = useState(true);

	const { setSnackBar, snackBar, setSnackBarOpen, loginUser, setLoadingOpen } = GlobalState();
	const disableEditAndApproval = loginUser.role?.SUPPLIER_VIEWER ? true : false;
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const tab = pathName;
	const setTab = (key) => {
		const qs = searchParams.toString();
		navigate(`../${key}${qs ? `?${qs}` : ""}`);
	};
	const [open, setOpen] = useState(false);
	const [approveOpen, setApproveOpen] = useState(false);
	const [unapproveOpen, setUnapproveOpen] = useState(false);
	const [rejectedData, setRejectedData] = useState({ type: "", remark: "", factory_id: null });
	const [allApproved, setAllApproved] = useState(true);
	const [selectedType, setSelectedType] = useState("");
	const [CompyOrFact, setCompyOrFact] = useState("");
	const [approveRemark, setApproveRemark] = useState("");
	const [unapproveRemark, setUnapproveRemark] = useState("");

	useEffect(() => {
		setAllApproved(!areAllLatestSectionsApproved(companyData, factories));
	}, [companyData, factories]);

	const applyApprovalResult = (row, isFactory, clearedFinalApproval) => {
		if (isFactory) {
			const updatedFactoryApproval = mergeApprovalRow(selectedFactory?.factoryApproval, row);
			const updatedFactory = { ...selectedFactory, factoryApproval: updatedFactoryApproval };
			setSelectedFactory(updatedFactory);
			setFactories((prev) =>
				(prev || []).map((fact) => (fact.id === updatedFactory.id ? updatedFactory : fact))
			);
		} else {
			setCompanyData((prev) => ({
				...prev,
				supplierApproval: mergeApprovalRow(prev?.supplierApproval, row),
				...(clearedFinalApproval ? { is_approved: false } : {}),
			}));
		}
		if (clearedFinalApproval) {
			setCompanyData((prev) => ({ ...prev, is_approved: false }));
		}
	};

	const callApprovalAction = async ({ action, type, isFactory, remark = "" }) => {
		const payload = {
			action,
			type,
			remark,
			supplier_id: isFactory ? null : companyData?.id,
			factory_id: isFactory ? selectedFactory?.id : null,
		};
		setLoadingOpen(true);
		try {
			const res = await axios.post(`/api/supplier/approval_action`, payload);
			applyApprovalResult(res.data.result, isFactory, res.data.cleared_final_approval);
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: `${isFactory ? "Factory" : "Supplier"} ${action.toLowerCase()} successful`,
				severity: "success",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			return true;
		} catch (err) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: parseErrMsgFromError(err) || "Unable to update approval",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			return false;
		} finally {
			setLoadingOpen(false);
		}
	};

	const handleClickOpen = (type, isFactory = false) => {
		setRejectedData({ type, remark: "", factory_id: isFactory ? selectedFactory?.id : null });
		setCompyOrFact(isFactory ? "Factory" : "Company");
		setOpen(true);
	};

	const handleApprovalOpen = (type, mainType) => {
		setSelectedType(type);
		setCompyOrFact(mainType);
		setApproveRemark("");
		setApproveOpen(true);
	};

	const handleUnapproveOpen = (type, mainType) => {
		setSelectedType(type);
		setCompyOrFact(mainType);
		setUnapproveRemark("");
		setUnapproveOpen(true);
	};

	const handleClose = () => {
		setOpen(false);
	};

	const handleSubmit = async () => {
		const ok = await callApprovalAction({
			action: APPROVAL_ACTIONS.REJECT,
			type: rejectedData.type,
			isFactory: !!rejectedData.factory_id,
			remark: rejectedData.remark,
		});
		if (ok) setOpen(false);
	};

	const handleTextArea = (e) => {
		setRejectedData({ ...rejectedData, remark: e.target.value });
	};

	const updateSupplierApi = async (apibody) => {
		setLoadingOpen(true);
		await axios
			.put(`/api/supplier/update/${companyData.id}`, apibody)
			.then(() => {
				setSnackBarOpen(true);
				setCompanyData({ ...companyData, is_approved: true });
				setSnackBar({
					...snackBar,
					message: "Supplier updated",
					severity: "success",
					anchorOrigin: { horizontal: "center", vertical: "top" },
				});
			})
			.catch(() => {
				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					message: "Unable to update supplier details ",
					severity: "error",
					anchorOrigin: { horizontal: "center", vertical: "top" },
				});
			});
		setLoadingOpen(false);
	};

	const handleAllApprovedButton = async () => {
		if (companyData?.is_approved === false) await updateSupplierApi({ is_approved: true });
		else {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Supplier is Already Approved ",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		}
	};

	const handleApprove = async () => {
		setApproveOpen(false);
		await callApprovalAction({
			action: APPROVAL_ACTIONS.APPROVE,
			type: selectedType,
			isFactory: CompyOrFact === "Factory",
			remark: approveRemark,
		});
		setApproveRemark("");
	};

	const handleUnapprove = async () => {
		setUnapproveOpen(false);
		await callApprovalAction({
			action: APPROVAL_ACTIONS.UNAPPROVE,
			type: selectedType,
			isFactory: CompyOrFact === "Factory",
			remark: unapproveRemark,
		});
		setUnapproveRemark("");
	};

	useEffect(() => {
		const fetch = async () => {
			const id = searchParams.get(supplierIdParamName);
			if (id) {
				setLoadingOpen(true);
				await axios
					.get(`/api/supplier/get_by_id?${supplierIdParamName}=${id}`)
					.then((res) => {
						let data = res.data;
						setCompanyData(data.result);
						setFactories(data.result.supplierFactories);
					})
					.catch(() => {
						setSnackBarOpen(true);
						setSnackBar({
							...snackBar,
							message: "Unable to Load Supplier Data",
							severity: "error",
							anchorOrigin: { horizontal: "center", vertical: "top" },
						});
					});
				setLoadingOpen(false);
			} else {
				navigate("/explore/supplier_dashboard");
			}
		};
		fetch();
	}, []);

	const tabComponents = [
		{
			...supplierDrawerElements.company_info,
			component: (
				<CompanyDetails
					pageData={companyData}
					setPageData={setCompanyData}
					isOnboarded={isOnboarded}
					setIsOnboarded={setIsOnboarded}
					foreignKey={foreignKeys.supplier}
					disableEdit={true}
					hidePageHeader
					handleReject={(type) => handleClickOpen(type, false)}
					updateSupplierStore={(type) => handleApprovalOpen(type, "Company")}
					handleUnapprove={(type) => handleUnapproveOpen(type, "Company")}
				/>
			),
		},
		{
			...supplierDrawerElements.all_factories,
			component: (
				<AllFactories
					factories={factories}
					setFactories={setFactories}
					selectedFactory={selectedFactory}
					setSelectedFactory={setSelectedFactory}
					companyData={companyData}
					setTab={setTab}
					disableEdit={true}
					hidePageHeader
				/>
			),
		},
		{
			...supplierDrawerElements.factory,
			component: (
				<FactoryDetails
					key={selectedFactory?.id}
					foreignKey={foreignKeys.factory}
					pageData={selectedFactory}
					setPageData={setSelectedFactory}
					companyData={companyData}
					setTab={setTab}
					disableEdit={true}
					hidePageHeader
					handleReject={(type) => handleClickOpen(type, true)}
					updateSupplierStore={(type) => handleApprovalOpen(type, "Factory")}
					handleUnapprove={(type) => handleUnapproveOpen(type, "Factory")}
				/>
			),
		},
		{
			...supplierDrawerElements.agreements,
			component: (
				<Agreements
					foreignKey={foreignKeys.factory}
					setPageData={setSelectedFactory}
					companyData={companyData}
					setTab={setTab}
					disableEdit={true}
					hidePageHeader
					handleReject={(type) => handleClickOpen(type, false)}
					updateSupplierStore={(type) => handleApprovalOpen(type, "Company")}
					handleUnapprove={(type) => handleUnapproveOpen(type, "Company")}
					setCompanyData={(data) => setCompanyData(data)}
				/>
			),
		},
		{
			...supplierDrawerElements.status,
			component: (
				<SupplierStatus
					foreignKey={foreignKeys.factory}
					allFactories={factories}
					setPageData={setSelectedFactory}
					companyData={companyData}
					setTab={setTab}
					hidePageHeader
				/>
			),
		},
	];

	const logeduser = loginUser.role?.FINANCE_VIEWER ? true : false;

	const headerByTab = {
		[supplierDrawerElements.company_info.key]: {
			title: companyData?.company_name || "Supplier",
			subtitle: "Company profile, documents, and contact information",
		},
		[supplierDrawerElements.all_factories.key]: {
			title: "Factories",
			subtitle: "Select a factory to view details, documents, and contacts",
		},
		[supplierDrawerElements.factory.key]: {
			title: selectedFactory?.name || "Factory",
			subtitle: companyData?.company_name ? `${companyData.company_name} · Factory details` : "Factory details",
		},
		[supplierDrawerElements.agreements.key]: {
			title: "Agreements",
			subtitle: "Download templates and upload signed vendor agreements",
		},
		[supplierDrawerElements.status.key]: {
			title: "Approval Status",
			subtitle: "Section-wise approval progress for company and factories",
		},
	};
	const activeHeader = headerByTab[tab] || {
		title: companyData?.company_name || "Supplier",
		subtitle: "",
	};

	return (
		<Box sx={{ width: "100%", bgcolor: "#F9FAFB", minHeight: "100%" }}>
			<Box
				sx={{
					width: "100%",
					px: { xs: 1.5, md: 2.5 },
					pt: 2,
					pb: { xs: 2, md: 2.5 },
					boxSizing: "border-box",
				}}>
				<Box sx={{ width: "100%", maxWidth: 1280, mx: "auto" }}>
					<SupplierPageHeader
						title={activeHeader.title}
						subtitle={activeHeader.subtitle}
						sx={{ mb: 2 }}
						actions={
							<>
								<Button
									startIcon={<KeyboardBackspaceRoundedIcon />}
									variant="outlined"
									size="small"
									onClick={() => navigate("/explore/supplier_dashboard")}
									sx={{
										textTransform: "none",
										fontWeight: 600,
										borderColor: supplierUi.borderStrong,
										color: supplierUi.textPrimary,
										borderRadius: 1.5,
									}}>
									Back
								</Button>
								{companyData?.is_approved ? (
									<SupplierStatusChip label="Approved" />
								) : (
									<Button
										variant="contained"
										size="small"
										disabled={allApproved || logeduser || disableEditAndApproval}
										onClick={() => handleAllApprovedButton()}
										sx={{
											textTransform: "none",
											fontWeight: 600,
											borderRadius: 1.5,
											boxShadow: "none",
											"&:hover": { boxShadow: "none" },
										}}>
										Final approve
									</Button>
								)}
							</>
						}
					/>
					{companyData &&
						tabComponents &&
						tabComponents.map((row) => (
							<Box key={row.key} display={row.key === tab ? "block" : "none"} sx={{ width: "100%" }}>
								{row.component}
							</Box>
						))}
				</Box>
			</Box>
			<Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" PaperProps={supplierModalPaperProps}>
				<SupplierModalHeader title="Rejection remark" subtitle="This remark will be saved on the approval cycle" />
				<DialogContent sx={{ px: { xs: 2, md: 3 }, pt: 2.5, pb: 1 }}>
					<TextField
						autoFocus
						fullWidth
						multiline
						minRows={4}
						required
						placeholder="Enter rejection remark"
						value={rejectedData.remark}
						onChange={(e) => handleTextArea(e)}
						sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
					/>
				</DialogContent>
				<DialogActions sx={{ px: { xs: 2, md: 3 }, py: 2, borderTop: `1px solid ${supplierUi.border}`, gap: 1 }}>
					<Button variant="outlined" onClick={handleClose} sx={modalCancelBtnSx}>
						Close
					</Button>
					<Button
						disabled={!rejectedData?.remark}
						variant="contained"
						onClick={handleSubmit}
						autoFocus
						sx={modalSaveBtnSx}>
						Submit
					</Button>
				</DialogActions>
			</Dialog>
			<Dialog open={approveOpen} onClose={() => setApproveOpen(false)} fullWidth maxWidth="sm" PaperProps={supplierModalPaperProps}>
				<SupplierModalHeader
					title="Approve"
					subtitle={`Confirm approval for ${CompyOrFact} ${suppliers_atrr[selectedType] || selectedType}`}
				/>
				<DialogContent sx={{ px: { xs: 2, md: 3 }, pt: 2.5, pb: 1 }}>
					<Typography variant="body2" sx={{ color: supplierUi.textSecondary, mb: 1.5, fontSize: 14 }}>
						{`Are you sure you want to approve the ${CompyOrFact} ${suppliers_atrr[selectedType] || selectedType}?`}
					</Typography>
					<TextField
						fullWidth
						multiline
						minRows={3}
						placeholder="Remark (optional)"
						value={approveRemark}
						onChange={(e) => setApproveRemark(e.target.value)}
						sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
					/>
				</DialogContent>
				<DialogActions sx={{ px: { xs: 2, md: 3 }, py: 2, borderTop: `1px solid ${supplierUi.border}`, gap: 1 }}>
					<Button variant="outlined" onClick={() => setApproveOpen(false)} sx={modalCancelBtnSx}>
						No
					</Button>
					<Button variant="contained" onClick={handleApprove} sx={modalSaveBtnSx}>
						Yes
					</Button>
				</DialogActions>
			</Dialog>
			<Dialog open={unapproveOpen} onClose={() => setUnapproveOpen(false)} fullWidth maxWidth="sm" PaperProps={supplierModalPaperProps}>
				<SupplierModalHeader
					title="Unapprove"
					subtitle="This will open a new pending approval cycle"
				/>
				<DialogContent sx={{ px: { xs: 2, md: 3 }, pt: 2.5, pb: 1 }}>
					<Typography variant="body2" sx={{ color: supplierUi.textSecondary, mb: 1.5, fontSize: 14 }}>
						{`Are you sure you want to unapprove the ${CompyOrFact} ${
							suppliers_atrr[selectedType] || selectedType
						}? This will open a new pending cycle.`}
					</Typography>
					<TextField
						fullWidth
						multiline
						minRows={3}
						placeholder="Remark (optional)"
						value={unapproveRemark}
						onChange={(e) => setUnapproveRemark(e.target.value)}
						sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
					/>
				</DialogContent>
				<DialogActions sx={{ px: { xs: 2, md: 3 }, py: 2, borderTop: `1px solid ${supplierUi.border}`, gap: 1 }}>
					<Button variant="outlined" onClick={() => setUnapproveOpen(false)} sx={modalCancelBtnSx}>
						No
					</Button>
					<Button variant="contained" onClick={handleUnapprove} sx={modalSaveBtnSx}>
						Yes
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}

export default SupplierUser;
